import React from 'react';
import './LegalPage.css';

const PrivacyPolicyPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">

        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p className="legal-meta">Last updated: March 21, 2026 &nbsp;·&nbsp; Effective date: March 21, 2026</p>
        </div>

        <div className="legal-body">

          <section>
            <h2>1. Introduction</h2>
            <p>
              MyDocAI ("we," "us," or "our") is committed to protecting the privacy of our users.
              This Privacy Policy explains how we collect, use, disclose, retain, and safeguard
              information when you use our web application, mobile application, and related services
              (collectively, the "Service"). It also describes your rights and choices with respect
              to your personal information.
            </p>
            <p>
              By creating an account or using the Service, you acknowledge that you have read,
              understood, and agree to the practices described in this Privacy Policy. If you do
              not agree, please discontinue use of the Service immediately.
            </p>
            <p>
              This Policy applies to all users of MyDocAI, including visitors to our website,
              registered account holders, and users accessing the Service through our mobile
              application. It does not apply to third-party websites, services, or applications
              that may be linked to or integrated with the Service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p>
              We collect several categories of information depending on how you interact with the Service.
            </p>
            <p>
              <strong>Account and Registration Information.</strong> When you register for an
              account, we collect your email address, name (if provided), and a hashed representation
              of your password. We use this information to create and manage your account, authenticate
              your identity, and communicate with you about the Service.
            </p>
            <p>
              <strong>Document Content.</strong> The core function of MyDocAI requires you to upload
              documents for AI processing. These documents may contain sensitive personal, financial,
              legal, or medical information depending on the nature of the files you choose to upload.
              We treat document content with a high degree of confidentiality as described in this Policy.
            </p>
            <p>
              <strong>Usage and Interaction Data.</strong> We automatically collect information about
              how you interact with the Service, including the features you use, queries you submit
              to our AI chat interface, pages visited, time spent on the Service, and actions taken
              within the application. This data helps us understand usage patterns and improve the
              Service.
            </p>
            <p>
              <strong>Technical and Device Information.</strong> We collect technical information
              about the device and network you use to access the Service, including IP address,
              browser type and version, operating system, device identifiers, time zone settings,
              and referring URLs. This information is used for security monitoring, fraud prevention,
              and aggregate analytics.
            </p>
            <p>
              <strong>Communications.</strong> If you contact our support team or submit feedback,
              we retain records of that correspondence, including the content of your messages and
              any attachments you provide.
            </p>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>
              We use the information we collect solely to provide and improve the Service and to
              fulfill our legal obligations. We do not use your information for advertising or
              sell it to third parties.
            </p>
            <p>
              <strong>Service Delivery.</strong> We use your account information to authenticate
              you, maintain your session, and ensure that your data is accessible only to you.
              Your document content is processed through our AI pipeline — including optical character
              recognition (OCR) and large language model (LLM) analysis — to generate the responses
              and outputs you request.
            </p>
            <p>
              <strong>AI Processing and Embeddings.</strong> To enable document question-and-answer
              and semantic search features, we generate vector embeddings from your document content
              and store them in a secure vector database. These embeddings allow us to retrieve
              relevant portions of your documents in response to your queries. Embeddings are tied
              to your account and are deleted when you delete the associated document.
            </p>
            <p>
              <strong>Transactional Communications.</strong> We use your email address to send you
              service-related communications, including account verification emails, password reset
              links, and important notices about the Service. These communications are necessary to
              operate the Service and cannot be opted out of while your account is active.
            </p>
            <p>
              <strong>Security and Fraud Prevention.</strong> We analyze usage and technical data
              to detect suspicious activity, prevent unauthorized access, investigate potential
              security incidents, and enforce our Terms of Service.
            </p>
            <p>
              <strong>Service Improvement.</strong> We analyze aggregated, de-identified usage
              patterns to understand how users interact with the Service, identify areas for
              improvement, and prioritize new features. We do not use your individual document
              content for this purpose.
            </p>
            <p>
              <strong>Legal Compliance.</strong> We may process your information to comply with
              applicable legal obligations, respond to lawful requests from government authorities,
              and enforce our agreements.
            </p>
          </section>

          <section>
            <h2>4. Document Storage, Encryption, and Retention</h2>
            <p>
              All documents you upload are encrypted at rest using AES-256 encryption. Encryption
              keys are managed through Azure Key Vault and are rotated periodically. Documents are
              stored in Microsoft Azure cloud infrastructure, which is hosted in data centers that
              comply with industry security certifications including ISO 27001 and SOC 2.
            </p>
            <p>
              Documents remain stored in our systems for as long as your account is active. You may
              delete individual documents at any time through the Service interface. Upon deletion,
              the document content, its associated vector embeddings, and any cached AI responses
              related to that document are permanently removed from our active systems within 30 days.
              Deleted document data may persist in encrypted backup archives for up to 90 days, after
              which it is permanently purged.
            </p>
            <p>
              If you delete your account, all documents associated with your account are treated
              as deleted and follow the same retention schedule described above. Account-level
              information (such as your email address and transaction records) may be retained for
              up to seven years where required by applicable law or for legitimate business purposes
              such as dispute resolution.
            </p>
          </section>

          <section>
            <h2>5. AI Processing and Third-Party Subprocessors</h2>
            <p>
              MyDocAI relies on third-party cloud services to deliver its AI capabilities. When you
              upload a document, portions of that document's content may be transmitted to these
              services for processing. We have contractual data processing agreements in place with
              each subprocessor requiring them to maintain appropriate security standards and to use
              your data only for the purpose of delivering the requested service.
            </p>
            <p>
              <strong>Microsoft Azure OpenAI Service.</strong> We use Azure OpenAI (GPT-4.1 and
              text-embedding-ada-002) to analyze document content and generate answers to your
              queries. Microsoft's enterprise data protection commitments apply to data processed
              through Azure OpenAI. Importantly, your document content is not used to train
              Microsoft's or OpenAI's foundation models.
            </p>
            <p>
              <strong>Azure AI Document Intelligence.</strong> We use Azure's Document Intelligence
              service (formerly Form Recognizer) to perform optical character recognition (OCR) on
              uploaded documents. This service extracts text and structural information from your
              files to make them queryable. As with Azure OpenAI, your data is not used to train
              Microsoft's models.
            </p>
            <p>
              <strong>SendGrid (Twilio).</strong> We use SendGrid to deliver transactional emails
              such as account verification and password reset messages. Your email address and the
              content of transactional emails are transmitted to SendGrid for delivery purposes.
            </p>
            <p>
              We do not use your documents or personal data with any other AI providers, analytics
              platforms, or marketing services. We do not permit subprocessors to use your data for
              any purpose other than providing the specific service we have engaged them for.
            </p>
          </section>

          <section>
            <h2>6. Data Sharing and Disclosure</h2>
            <p>
              We do not sell, rent, or trade your personal information to any third party. We do not
              share your information with advertisers or data brokers. We limit data sharing strictly
              to the circumstances described below.
            </p>
            <p>
              <strong>Subprocessors.</strong> As described in Section 5, we share data with our
              technology subprocessors (Azure, SendGrid) as necessary to operate the Service. These
              transfers are governed by data processing agreements that impose confidentiality and
              security obligations.
            </p>
            <p>
              <strong>Legal Obligations.</strong> We may disclose your information if we are required
              to do so by applicable law, court order, subpoena, or other legal process. Where
              permitted by law, we will notify you of such a request before disclosing your data
              unless we are prohibited from doing so or believe that notification would impede an
              ongoing investigation or create an imminent risk of harm.
            </p>
            <p>
              <strong>Protection of Rights.</strong> We may disclose information when we believe in
              good faith that disclosure is necessary to protect the rights, property, or safety of
              MyDocAI, our users, or others — for example, to investigate suspected fraud or prevent
              unauthorized access to the Service.
            </p>
            <p>
              <strong>Business Transfers.</strong> In the event of a merger, acquisition, sale of
              assets, or other business transfer involving MyDocAI, your information may be
              transferred as part of that transaction. We will provide notice via email or a
              prominent notice on the Service before your information becomes subject to a
              materially different privacy policy.
            </p>
          </section>

          <section>
            <h2>7. Data Security</h2>
            <p>
              We implement technical and organizational measures designed to protect your information
              against unauthorized access, loss, destruction, or alteration. These measures include
              end-to-end encryption of document content using AES-256, TLS 1.2 or higher for all
              data in transit, secret management via Azure Key Vault, JSON Web Token (JWT) based
              authentication with configurable token expiry, and role-based access controls that
              ensure your documents are never accessible to other users.
            </p>
            <p>
              All user data is logically isolated — every query to our database is scoped by your
              authenticated user ID, making it technically impossible for one user to access
              another user's documents through the normal Service interface.
            </p>
            <p>
              While we take these precautions, no system is completely secure. We cannot guarantee
              the absolute security of your information, and you use the Service at your own risk.
              In the event of a data breach that affects your personal information, we will notify
              you as required by applicable law.
            </p>
          </section>

          <section>
            <h2>8. Session Management and Cookies</h2>
            <p>
              MyDocAI uses JSON Web Tokens (JWTs) for session management. These tokens are stored
              client-side and are transmitted with each authenticated request. Tokens expire after
              a configured period of inactivity, at which point you will be required to log in again.
            </p>
            <p>
              We use essential session cookies required for the Service to function. We do not use
              advertising cookies, cross-site tracking cookies, or third-party analytics cookies.
              We do not participate in ad networks or behavioral tracking programs.
            </p>
          </section>

          <section>
            <h2>9. Your Rights and Choices</h2>
            <p>
              Depending on your jurisdiction, you may have certain rights with respect to your
              personal information. We honor these rights regardless of where you are located.
            </p>
            <p>
              <strong>Right of Access.</strong> You may request a copy of the personal information
              we hold about you, including your account details and a list of documents associated
              with your account.
            </p>
            <p>
              <strong>Right to Rectification.</strong> You may request correction of inaccurate or
              incomplete personal information. You can update your account email and display name
              directly through the Service.
            </p>
            <p>
              <strong>Right to Erasure.</strong> You may request deletion of your personal data and
              account. You may also delete individual documents at any time through the Service
              interface. Account deletion removes your profile, all documents, embeddings, and
              chat history associated with your account.
            </p>
            <p>
              <strong>Right to Data Portability.</strong> You may request an export of your
              personal data in a structured, machine-readable format. Contact us at the address
              below to initiate a data export request.
            </p>
            <p>
              <strong>Right to Object or Restrict Processing.</strong> You may object to or request
              restriction of certain processing activities. Note that some processing is necessary
              to provide the Service and cannot be restricted without terminating your account.
            </p>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@mydocai.org">support@mydocai.org</a>. We will respond to
              verifiable requests within 30 days.
            </p>
          </section>

          <section>
            <h2>10. California Privacy Rights (CCPA)</h2>
            <p>
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA). You have
              the right to know what personal information we collect and how we use it, the right
              to delete personal information, the right to opt out of the sale or sharing of
              personal information (we do not sell or share personal information), and the right
              to non-discrimination for exercising your privacy rights.
            </p>
            <p>
              In the preceding 12 months, we have not sold or shared personal information with
              third parties for cross-context behavioral advertising. To submit a CCPA request,
              contact us at <a href="mailto:support@mydocai.org">support@mydocai.org</a>.
            </p>
          </section>

          <section>
            <h2>11. International Data Transfers</h2>
            <p>
              MyDocAI's infrastructure is hosted in Microsoft Azure data centers, primarily located
              in the United States. If you access the Service from outside the United States, your
              personal information will be transferred to and processed in the United States or other
              countries where our subprocessors operate. These countries may have data protection
              laws that differ from those in your jurisdiction.
            </p>
            <p>
              We take steps to ensure that such transfers are conducted in compliance with applicable
              data protection laws, including through the use of standard contractual clauses where
              required.
            </p>
          </section>

          <section>
            <h2>12. Children's Privacy</h2>
            <p>
              The Service is not directed to children under the age of 13, and we do not knowingly
              collect personal information from children under 13. If we learn that we have
              inadvertently collected personal information from a child under 13, we will take
              prompt steps to delete that information. If you believe a child under 13 has provided
              personal information to us, please contact us at{' '}
              <a href="mailto:support@mydocai.org">support@mydocai.org</a>.
            </p>
          </section>

          <section>
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our
              practices, technology, legal requirements, or other factors. When we make changes,
              we will revise the "Last updated" date at the top of this page. For material changes,
              we will provide additional notice, such as an email to the address associated with
              your account or a prominent notice within the Service.
            </p>
            <p>
              Your continued use of the Service after the effective date of any updated Privacy
              Policy constitutes your acceptance of the updated terms. If you do not agree to the
              updated Policy, you should stop using the Service and may request deletion of your
              account.
            </p>
          </section>

          <section>
            <h2>14. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests relating to this Privacy Policy or our
              handling of your personal information, please contact us at:
            </p>
            <p>
              MyDocAI &nbsp;·&nbsp; <a href="mailto:support@mydocai.org">support@mydocai.org</a>
            </p>
            <p>
              We take privacy inquiries seriously and will respond to all verifiable requests
              within 30 days.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
